// Block D20 Part A — frontend mirror of backend lib/taskTime.js (taskInstantUtc).
//
// Tasks store their time as a BARE WALL-CLOCK string (no timezone). "Overdue" must be judged
// by reinterpreting that wall-clock in the user's STORED timezone (currentUser.timezone) — NOT
// the browser's — so the list badge, the Overdue filter and the chat all agree even when the
// browser timezone differs (travel). This computes the same true-UTC instant the backend does.
//
// Where it differs from the backend taskInstantUtc:
//  - It must be GIVEN the stored timezone (the backend reads user.timezone from the DB).
//  - It parses the wall-clock digits via `+ 'Z'` (not `new Date(str)`) precisely so a non-UTC
//    browser doesn't shift the digits — the backend can use `new Date(str)` only because its
//    server clock is UTC.
//  - A date-only value (no time component) is treated as end-of-day 23:59:59; the backend's
//    dateFromTask yields midnight for a date-only *deadline* specifically. This sub-day gap
//    only matters for date-only deadlines (rare — most tasks carry a time).

function normalizeUserTimezone(tz) {
  return (!tz || tz === 'UTC') ? 'Europe/London' : tz; // legacy shim, same as the backend
}

// Reinterpret the wall-clock digits of `wallClockDate` as `timezone` → true UTC instant.
function wallClockToUtcDate(wallClockDate, timezone) {
  const s = wallClockDate.toISOString().slice(0, 19);
  const asIfUtc = new Date(s + 'Z');
  const localStr = new Intl.DateTimeFormat('sv', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(asIfUtc).replace(' ', 'T');
  const correction = asIfUtc.getTime() - new Date(localStr + 'Z').getTime();
  return new Date(asIfUtc.getTime() + correction);
}

// True UTC instant for a task's stored wall-clock, in the user's stored timezone.
// Field precedence mirrors the backend dateFromTask: deadline → datetime → date.
export function taskInstant(task, userTimezone) {
  if (!task) return null;
  const tz = normalizeUserTimezone(userTimezone);
  let raw = task.deadline || task.datetime || task.date || null;
  if (!raw) return null;
  let wallClockStr = String(raw).slice(0, 19);
  if (!wallClockStr.includes('T')) wallClockStr += 'T23:59:59'; // date-only → end of day
  const asUtc = new Date(wallClockStr + 'Z'); // '+Z' keeps the raw digits regardless of browser tz
  if (isNaN(asUtc.getTime())) return null;
  return wallClockToUtcDate(asUtc, tz);
}

// The single overdue predicate. `deadline || datetime || date` (a task with a time but no
// explicit deadline still becomes overdue once that time passes); a task with no date is never
// overdue (fixes the old `new Date(null)` → 1970 → always-overdue bug).
export function isTaskOverdue(task, userTimezone) {
  if (!task || task.completed) return false;
  const d = taskInstant(task, userTimezone);
  return !!d && d.getTime() < Date.now();
}
